import React from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiEye, FiEdit2 } from 'react-icons/fi';
import dayjs from 'dayjs';
import {
  SaktiPaginationMeta,
  SaktiBccListItem,
  SaktiBaListItem,
} from '../types';
import { SaktiBccTabKey } from '../hooks/useSaktiBccDashboard';
import { SAKTI_UI_SIZE } from '../uiSizes';

interface SaktiBccTabsTableProps {
  loading: boolean;
  activeTab: SaktiBccTabKey;
  rows: (SaktiBccListItem | SaktiBaListItem)[];
  pagination: SaktiPaginationMeta;
  onTabChange: (tab: SaktiBccTabKey) => void;
  onPageChange: (page: number) => void;
  onViewDetail: (id: string) => void;
  onEdit: (id: string) => void;
}

const getPaginationButtons = (currentPage: number, totalPages: number) => {
  const visibleRange = 3;
  const startPage = Math.max(1, currentPage - 1);
  const endPage = Math.min(totalPages, startPage + visibleRange - 1);
  const pages: number[] = [];
  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page);
  }
  return pages;
};

const SaktiBccTabsTable: React.FC<SaktiBccTabsTableProps> = ({
  loading,
  activeTab,
  rows,
  pagination,
  onTabChange,
  onPageChange,
  onViewDetail,
  onEdit,
}) => {
  const pageButtons = getPaginationButtons(pagination.page, pagination.total_pages);
  const isBaList = activeTab === 'ba_list';

  return (
    <VStack align="stretch" spacing={0}>
      <HStack spacing={2} borderBottomWidth="1px" borderBottomColor="customGreen.500" mb={3} overflowX="auto">
        {[
          { k: 'all', label: 'Semua BCC' },
          { k: 'restan', label: 'Belum Diangkut' },
          { k: 'ba', label: 'BCC dengan BA' },
          { k: 'ba_restan', label: 'BA Restan Aktif' },
          { k: 'ba_list', label: 'Daftar Berita Acara' },
        ].map((t) => (
          <Button
            key={t.k}
            size="sm"
            variant="ghost"
            borderBottomWidth="3px"
            borderBottomColor={activeTab === t.k ? 'customGreen.500' : 'transparent'}
            borderRadius={0}
            color={activeTab === t.k ? 'customGreen.500' : 'gray.700'}
            fontSize={SAKTI_UI_SIZE.tabTitle}
            fontWeight="bold"
            onClick={() => onTabChange(t.k as SaktiBccTabKey)}
            minW="max-content"
          >
            {t.label}
          </Button>
        ))}
      </HStack>

      <Skeleton isLoaded={!loading} borderRadius={SAKTI_UI_SIZE.cardRadius}>
        <Box
          borderRadius={SAKTI_UI_SIZE.cardRadius}
          overflow="hidden"
          borderWidth="1px"
          borderColor="gray.200"
          bg="white"
        >
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg="customGreen.500">
                {isBaList ? (
                  <Tr>
                    <Th color="white">BA No.</Th>
                    <Th color="white">Tipe</Th>
                    <Th color="white" isNumeric>Jml BCC</Th>
                    <Th color="white" isNumeric>Total Jjg</Th>
                    <Th color="white" isNumeric>Total Brd</Th>
                    <Th color="white">Lokasi</Th>
                    <Th color="white">Catatan</Th>
                    <Th color="white">Aksi</Th>
                  </Tr>
                ) : (
                  <Tr>
                    <Th color="white">BCC No.</Th>
                    <Th color="white">Pemanen</Th>
                    <Th color="white">Blok / TPH</Th>
                    <Th color="white" isNumeric>Jjg</Th>
                    <Th color="white" isNumeric>Mentah</Th>
                    <Th color="white" isNumeric>Brondolan</Th>
                    <Th color="white">Status</Th>
                    <Th color="white">Aksi</Th>
                  </Tr>
                )}
              </Thead>
              <Tbody>
                {rows.length === 0 ? (
                  <Tr>
                    <Td colSpan={10}>
                      <Text color="gray.500" textAlign="center" py={4} fontSize="sm">
                        Tidak ada data
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  rows.map((row) => {
                    if (isBaList) {
                      const ba = row as SaktiBaListItem;
                      return (
                        <Tr key={ba.id}>
                          <Td>
                            <Text fontWeight="bold">{ba.ba_nbr}</Text>
                            <Text fontSize="xs" color="gray.500">{dayjs(ba.ba_date).format('DD/MM/YYYY')}</Text>
                          </Td>
                          <Td>
                            <Badge colorScheme={ba.report_type === 9 ? 'orange' : 'blue'}>
                              {ba.report_type === 9 ? 'Restan' : `Tipe ${ba.report_type}`}
                            </Badge>
                          </Td>
                          <Td isNumeric>{ba.bcc_count}</Td>
                          <Td isNumeric>{ba.total_bunch}</Td>
                          <Td isNumeric>{ba.total_loose_fruit.toFixed(1)}</Td>
                          <Td>
                            <Text fontSize="xs">{ba.estate_name}</Text>
                            <Text fontSize="xs" color="gray.500">{ba.division_name}</Text>
                          </Td>
                          <Td maxW="200px" isTruncated>{ba.notes}</Td>
                          <Td>
                            <HStack spacing={1}>
                              <IconButton size="xs" icon={<FiEye />} aria-label="View" onClick={() => onViewDetail(ba.id)} />
                              <IconButton size="xs" icon={<FiEdit2 />} aria-label="Edit" onClick={() => onEdit(ba.id)} />
                            </HStack>
                          </Td>
                        </Tr>
                      );
                    } else {
                      const bcc = row as SaktiBccListItem;
                      return (
                        <Tr key={bcc.id}>
                          <Td>
                            <Text fontWeight="bold">{bcc.harvest_nbr}</Text>
                            <Text fontSize="xs" color="gray.500">{dayjs(bcc.harvest_date).format('DD/MM/YYYY')}</Text>
                          </Td>
                          <Td>
                            <Text fontWeight="bold">{bcc.harvester_name}</Text>
                            <Text fontSize="xs" color="gray.500">{bcc.harvester_nip}</Text>
                          </Td>
                          <Td>{bcc.block_code} / {bcc.tph_code}</Td>
                          <Td isNumeric><strong>{bcc.bunch_qty}</strong></Td>
                          <Td isNumeric>{bcc.unripe_qty}</Td>
                          <Td isNumeric>{bcc.loose_fruit_qty.toFixed(1)}</Td>
                          <Td>
                            {bcc.transport_id ? (
                              <Badge colorScheme="green">Terkirim</Badge>
                            ) : (
                              <Badge colorScheme="orange">Restan</Badge>
                            )}
                          </Td>
                          <Td>
                            <HStack spacing={1}>
                              <IconButton size="xs" icon={<FiEye />} aria-label="View" onClick={() => onViewDetail(bcc.id)} />
                              <IconButton size="xs" icon={<FiEdit2 />} aria-label="Edit" onClick={() => onEdit(bcc.id)} />
                            </HStack>
                          </Td>
                        </Tr>
                      );
                    }
                  })
                )}
              </Tbody>
            </Table>
          </Box>

          <HStack px={4} py={3} bg="customGreen.500" color="white" justify="space-between">
            <Text fontSize="xs" fontWeight="semibold">
              SHOWING {rows.length} OF {pagination.total_items.toLocaleString('id-ID')}
            </Text>
            <HStack spacing={1}>
              <IconButton
                aria-label="Previous"
                icon={<FiChevronLeft />}
                size="sm"
                variant="outline"
                isDisabled={pagination.page <= 1}
                onClick={() => onPageChange(pagination.page - 1)}
              />
              {pageButtons.map((p) => (
                <Button
                  key={p}
                  size="sm"
                  variant={p === pagination.page ? 'solid' : 'outline'}
                  bg={p === pagination.page ? 'white' : 'transparent'}
                  color={p === pagination.page ? 'customGreen.500' : 'white'}
                  onClick={() => onPageChange(p)}
                >
                  {p}
                </Button>
              ))}
              <IconButton
                aria-label="Next"
                icon={<FiChevronRight />}
                size="sm"
                variant="outline"
                isDisabled={pagination.page >= pagination.total_pages}
                onClick={() => onPageChange(pagination.page + 1)}
              />
            </HStack>
          </HStack>
        </Box>
      </Skeleton>
    </VStack>
  );
};

export default SaktiBccTabsTable;
